'use client';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useEffect, useMemo, useRef, useState } from 'react';
import style from './style.module.css';

export type Option = {
  label: string;
  value: string;
};

export interface SearchableCheckboxFilterProps {
  placeholderSeach: string;
  titleSearch: string;
  name: string;
  allOptions: Option[];
  selectedOptions: Option[];
  onOptionSelect?: (option: Option) => void;
  onOptionDeselect?: (option: Option) => void;
  disabled?: boolean;
}

const SearchableCheckboxFilter: React.FC<SearchableCheckboxFilterProps> = ({
  placeholderSeach,
  titleSearch,
  name,
  allOptions,
  selectedOptions = [],
  onOptionSelect,
  onOptionDeselect,
  disabled,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<Option[]>([]);
  const allOptionsValues = allOptions.map((option) => option.value);
  const selectedOptionsCurrent = selectedOptions.filter((selected) =>
    allOptionsValues.includes(selected.value)
  );
  const selectedOptionsValues = useMemo(
    () => selectedOptionsCurrent.map(({ value }) => value),
    [selectedOptionsCurrent]
  );
  const selectedOptionsLabels = useMemo(
    () => selectedOptionsCurrent.map(({ label }) => label),
    [selectedOptionsCurrent]
  );
  let placeholder = placeholderSeach;
  const containerRef = useRef(null);
  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };

  if (selectedOptionsLabels.length === 1) {
    placeholder = selectedOptionsLabels[0];
  } else if (selectedOptionsLabels.length > 1) {
    placeholder = `${selectedOptionsLabels
      .slice(0, selectedOptionsLabels.length - 1)
      .join(', ')} e ${
      selectedOptionsLabels[selectedOptionsLabels.length - 1]
    }`;
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    const selectedOption = allOptions.find((option) => option.value === value);
    if (checked) {
      onOptionSelect(selectedOption);
    } else {
      onOptionDeselect(selectedOption);
    }
  };

  const handleClickOutside = (event) => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setShowOptions(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (searchTerm === undefined) {
      return;
    }

    const lowercasedSearchTerm = searchTerm.toLowerCase();
    const filtered = allOptions
      .filter((option) => {
        if (!option.label) return false;

        const isOptionPresentInUrl = option.label
          .toLowerCase()
          .includes(lowercasedSearchTerm);
        const isOptionNotEmpty = !!option.label.trim();
        return isOptionPresentInUrl && isOptionNotEmpty;
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    setFilteredOptions(filtered);
  }, [searchTerm, allOptions]);

  return (
    <div ref={containerRef} className={style.searchContainer}>
      <span className={style.titleSearch}>{titleSearch}</span>
      <div className={style.inputWithIcon}>
        <input
          className={style.inputSearch}
          type="search"
          placeholder={placeholder}
          readOnly
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={toggleOptions}
          disabled={disabled}
        />
        {showOptions ? (
          <KeyboardArrowUpIcon onClick={toggleOptions} />
        ) : (
          <KeyboardArrowDownIcon onClick={toggleOptions} />
        )}
      </div>
      {showOptions && (
        <div className={style.searchOptions}>
          {filteredOptions.map((option) => (
            <label key={option.value}>
              <input
                className={style.inputCheckbox}
                type="checkbox"
                value={option.value}
                name={name}
                checked={selectedOptionsValues.includes(option.value)}
                onChange={handleCheckboxChange}
                disabled={disabled}
              />
              <span className={style.textInput}>{option.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchableCheckboxFilter;
